import React, { useEffect, useMemo, useState } from 'react';
import { extractHeadings } from '../../utils/wikiHeadings';

// 右侧目录（§7.3）。
//
// **浮动**在正文列右边的空白里，不是布局的第三栏 —— 窄屏直接消失，正文列的
// 位置一动不动。这是它与「三栏布局」的关键区别：如果它占一栏，窄屏下要么正文
// 被挤窄、要么整栏藏掉而正文突然变宽，两种都会让同一篇文章在两个宽度下读起来
// 不是一个东西。
//
// 目录不入库：它是正文的纯函数（抓 h2 / h3 的算法在 utils/wikiHeadings.ts，
// 与 Markdown.tsx 挂 id 用的是同一个函数）。存下来就要维护它和正文的一致性。
type TocProps = { bodyMd: string | null };

// 少于两条就不显示。一条目录不提供任何导航价值 —— 它只是把标题重复了一遍，
// 而且会让人以为这篇文章只有一节。
const MIN_HEADINGS = 2;

const Toc: React.FC<TocProps> = ({ bodyMd }) => {
  const headings = useMemo(() => extractHeadings(bodyMd), [bodyMd]);
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    if (headings.length < MIN_HEADINGS) return undefined;

    // root 用默认的视口，不去找那个滚动容器：标题在滚动容器里，但**也**在视口里
    // ——IntersectionObserver 对视口的判定同样成立，而去拿容器 ref 就要把
    // WikiLayout 的内部结构漏给这个组件。
    //
    // rootMargin 把判定区间压成「视口上缘往下 120px 起、往下到 40% 处」：
    // 不压的话满屏都算「可见」，于是永远是最后一个标题在亮。
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length > 0) setActive(visible[0].target.id);
      },
      { rootMargin: '-120px 0px -60% 0px', threshold: 0 },
    );

    const nodes = headings
      .map((heading) => document.getElementById(heading.id))
      .filter((node): node is HTMLElement => node !== null);
    nodes.forEach((node) => observer.observe(node));

    return () => observer.disconnect();
    // headings 是 useMemo 出来的稳定引用，正文变了才重建 —— 而正文变了确实要
    // 重新挂一遍（标题的 DOM 节点是新的）。
  }, [headings]);

  if (headings.length < MIN_HEADINGS) return null;

  return (
    // top-24 = 96px，与设计稿的 `.toc { top: 96px }` 同值 —— 去掉全站 Header 之后
    // 这一页的几何与那张稿完全一致（顶条 45px + `.doc` 的 40px 上内边距，
    // 于是标题从 85px 起），所以稿子的数值直接成立。
    //
    // 一开始写的是 top-40（160px），因为当时上面还有 64px 的 Header。Header 去掉
    // 之后它没跟着改，结果目录整块比标题低了 75px —— 看起来像掉在正文中间。
    //
    // toc: 是 1180px 的具名断点（tailwind.config.js）。窄于它整块消失。
    <nav
      className="fixed right-7 top-24 hidden w-[190px] text-[12.5px] text-todo-muted toc:block"
      aria-label="目录"
    >
      <div className="mb-1.5 text-[11.5px] font-semibold tracking-wide">目录</div>
      {headings.map((heading) => (
        <a
          key={heading.id}
          href={`#${heading.id}`}
          onClick={(event) => {
            // 自己滚，不让浏览器处理 hash：默认行为会往 history 里推一条，
            // 于是读完一篇文章按后退要按七次才回得到列表页。
            event.preventDefault();
            document.getElementById(heading.id)?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
            setActive(heading.id);
          }}
          className={[
            'block border-l-2 py-[3px] pl-2.5 hover:text-todo-ink2',
            // h3 再缩进一档。层级只有两级，所以用缩进区分就够，不需要字号也变。
            heading.level === 3 ? 'pl-5' : '',
            active === heading.id
              ? 'border-todo-ink2 text-todo-ink'
              : 'border-transparent',
          ].join(' ')}
        >
          {heading.text}
        </a>
      ))}
    </nav>
  );
};

export default Toc;
